import { useEffect, useState } from 'react';
import { useSplash } from './SplashContext';
import { useFirstSplashCover } from './useFirstSplashCover';
import { SPLASH_HOLD_ABSOLUTE_MS } from './splashHoldConfig';

/**
 * useSplashHold(active) — ask the splash to WAIT for a decision this screen is
 * still making (KAN-562: "show the role welcome?"), bounded:
 *  - it only ever extends this screen's FIRST splash cover (useFirstSplashCover:
 *    up at mount, or raised right after it) — it never raises one;
 *  - it lingers at most SPLASH_HOLD_MAX_MS past every other splash signal
 *    (SplashProvider), and is dropped SPLASH_HOLD_ABSOLUTE_MS after that cover
 *    was first seen;
 *  - it EXPIRES the moment the splash leaves 'covered' (whatever kept it up):
 *    from then on the screen is visible and the caller's normal rules apply;
 *  - once released (`active` false while covered) it never comes back for this
 *    mount — the answer arrived, or it was NO.
 * Past either bound everything behaves exactly as without the hold. `optional`
 * so a screen rendered without SplashProvider (unit tests) no-ops.
 * Returns whether the hold is still live (false once released or expired).
 */
export function useSplashHold(active) {
  const covered = useFirstSplashCover();
  const [expired, setExpired] = useState(false);
  const live = Boolean(active) && covered && !expired;

  useEffect(() => {
    if (!covered || expired) return undefined;
    const t = setTimeout(() => setExpired(true), SPLASH_HOLD_ABSOLUTE_MS);
    return () => clearTimeout(t);
  }, [covered, expired]);

  // Released once → never re-armed (a late flip back to "undecided" must not hold again).
  useEffect(() => { if (covered && !active) setExpired(true); }, [covered, active]);

  useSplash(live, { immediate: true, optional: true, hold: true });
  return live;
}

export default useSplashHold;
